import { useCallback, useEffect, useState } from "react";
import { LifeBuoy, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TicketThread } from "@/components/TicketThread";
import { api, errText } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { hasProfanity, PROFANITY_WARNING } from "@/lib/profanity";

export function TicketList({ items, active, onSelect }) {
  if (items.length === 0) return <p className="p-6 text-sm text-[#6b8a7b] text-center" data-testid="tickets-empty">Обращений нет</p>;
  return items.map((t) => (
    <button key={t.id} onClick={() => onSelect(t.id)} data-testid={`ticket-item-${t.id}`}
      className={`w-full text-left p-4 border-b border-[#d1e7dd] transition-colors ${active === t.id ? "bg-[#e8f5ed]" : "hover:bg-[#f3f9f5]"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm text-[#0b2e20] truncate">{t.subject}</span>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${t.status === "open" ? "bg-[#c8e6d3] text-[#1f5f42]" : "bg-slate-100 text-slate-500"}`}>{t.status === "open" ? "Открыто" : "Закрыто"}</span>
      </div>
      <div className="text-xs text-[#6b8a7b] mt-1">{t.author} · {formatDate(t.updated_at)}{t.last_from_staff ? " · есть ответ" : ""}</div>
    </button>
  ));
}

function NewTicket({ onCreated }) {
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (hasProfanity(subject, text)) { toast.warning(PROFANITY_WARNING); return; }
    try {
      const { data } = await api.post("/support", { subject, text });
      setSubject(""); setText(""); toast.success("Обращение отправлено"); onCreated(data.id);
    } catch (err) { toast.error(errText(err)); }
  };
  return (
    <form onSubmit={submit} className="p-6 space-y-3" data-testid="new-ticket-form">
      <h3 className="font-display font-bold text-lg text-[#0b2e20]">Новое обращение</h3>
      <Input required minLength={2} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Тема" data-testid="new-ticket-subject-input" />
      <Textarea required rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Опишите проблему или вопрос" data-testid="new-ticket-text-input" />
      <Button type="submit" className="rounded-full bg-[#1f5f42] hover:bg-[#174731]" data-testid="new-ticket-submit-button">Отправить</Button>
    </form>
  );
}

export default function SupportPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const load = useCallback(() => { api.get("/support/mine").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 rise">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#1f5f42] text-[#a3e6be] grid place-items-center"><LifeBuoy className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-4xl font-extrabold text-[#0b2e20]">Служба поддержки</h1>
            <p className="text-sm text-[#4b6b5c]">Вам ответит модератор или администратор</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setActive(null)} className="rounded-full" data-testid="new-ticket-button"><Plus className="h-4 w-4 mr-1" />Новое обращение</Button>
      </div>
      <div className="mt-6 sm:mt-8 grid lg:grid-cols-[320px_1fr] gap-4">
        <div className={`rounded-2xl bg-white border border-[#d1e7dd] overflow-hidden max-h-[600px] overflow-y-auto scrollbar-thin order-2 lg:order-1 ${active ? "hidden lg:block" : ""}`} data-testid="my-tickets-list">
          <TicketList items={items} active={active} onSelect={setActive} />
        </div>
        <div className="rounded-2xl bg-white border border-[#d1e7dd] overflow-hidden order-1 lg:order-2">
          {active && <button onClick={() => setActive(null)} className="lg:hidden px-5 pt-4 text-sm font-semibold text-[#2e7d56]" data-testid="ticket-back-button">← К списку</button>}
          {active ? <TicketThread key={active} ticketId={active} onChange={load} /> : <NewTicket onCreated={(id) => { load(); setActive(id); }} />}
        </div>
      </div>
    </div>
  );
}
